import { useState, useEffect } from "react";
import { api, apiErr } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { Empty } from "@/components/Shell";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StudentInstallmentsModal } from "@/components/PaymentModality";
import { toast } from "sonner";
import { GraduationCap, FileDown, Wallet, Plus, Trash2 } from "lucide-react";

const GRADES = ["Chez-Kids 1", "Chez-Kids 2", "Chez-Kids 3", "CP1", "CP2", "CE1", "CE2", "CM1", "CM2",
  "7ème Année Fondamentale", "8ème Année Fondamentale", "9ème Année Fondamentale", "NS1", "NS2", "NS3", "NS4"];

export default function Scholarships() {
  const { L } = useLang();
  const [level, setLevel] = useState("all");
  const [type, setType] = useState("all");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [students, setStudents] = useState([]);
  const [addId, setAddId] = useState("");
  const [addType, setAddType] = useState("full");
  const [addAmount, setAddAmount] = useState("");
  const [payStudent, setPayStudent] = useState(null);

  const loadStudents = async () => { try { setStudents(await api.get("/students")); } catch (e) { if (process.env.NODE_ENV !== "production") console.error(e); } };
  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (level !== "all") params.level = level;
      if (type !== "all") params.type = type;
      setData(await api.get("/finance/scholarships", { params }));
    } catch (e) { toast.error(L("Failed to load", "Échec du chargement")); }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [level, type]);
  useEffect(() => { loadStudents(); /* eslint-disable-next-line */ }, []);

  const setScholarship = async (id, v, amount) => {
    try {
      const body = v === "none" ? { scholarship: null } : v === "special" ? { scholarship: "special", amount: amount === "" || amount == null ? null : Number(amount) } : { scholarship: v };
      await api.raw.patch(`/students/${id}/scholarship`, body);
      toast.success(L("Scholarship updated", "Bourse mise à jour"));
      load(); loadStudents();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const addScholarship = async () => {
    if (!addId) { toast.error(L("Pick a student", "Choisissez un élève")); return; }
    await setScholarship(addId, addType, addAmount);
    setAddId(""); setAddAmount("");
  };

  const exportPdf = async () => {
    try {
      const qs = new URLSearchParams();
      if (level !== "all") qs.set("level", level);
      if (type !== "all") qs.set("type", type);
      const r = await api.raw.get(`/finance/scholarships/pdf?${qs.toString()}`, { responseType: "blob" });
      const u = URL.createObjectURL(r.data); const a = document.createElement("a");
      a.href = u; a.download = "bourses_ICDH.pdf"; a.click(); URL.revokeObjectURL(u);
    } catch (e) { toast.error(L("Export failed", "Échec de l'export")); }
  };

  const rows = data?.students || [];
  const accepted = (students || []).filter((s) => s.status === "accepted");
  const amountOf = (id) => (students.find((s) => s.id === id) || {}).scholarship_amount ?? "";

  return (
    <div className="animate-fade-up" data-testid="scholarships-report">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-heading font-extrabold text-2xl flex items-center gap-2"><GraduationCap className="h-6 w-6 text-primary" />{L("Scholarships", "Bourses")}</h2>
          <p className="text-muted-foreground text-sm">{L("Add students to scholarships, set full / half / special, and waive installments 1–4.", "Ajoutez des élèves aux bourses, définissez boursier / demi-bourse / spécial, et exonérez les versements 1 à 4.")}</p>
        </div>
        <Button onClick={exportPdf} data-testid="sch-export-pdf"><FileDown className="h-4 w-4 mr-1" />{L("Export PDF", "Exporter en PDF")}</Button>
      </div>

      {/* Add / grant a scholarship */}
      <div className="bg-card border border-border rounded-lg p-4 mb-6" data-testid="sch-add-panel">
        <p className="font-semibold text-sm mb-2 flex items-center gap-1"><Plus className="h-4 w-4" />{L("Grant a scholarship", "Accorder une bourse")}</p>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <p className="text-xs text-muted-foreground mb-1">{L("Student", "Élève")}</p>
            <Select value={addId} onValueChange={setAddId}>
              <SelectTrigger className="w-64" data-testid="sch-add-student"><SelectValue placeholder={L("Select a student", "Choisir un élève")} /></SelectTrigger>
              <SelectContent>{accepted.map((s) => <SelectItem key={s.id} value={s.id}>{s.first_name} {s.last_name} · {s.grade_level}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <p className="text-xs text-muted-foreground mb-1">{L("Type", "Type")}</p>
            <Select value={addType} onValueChange={setAddType}>
              <SelectTrigger className="w-40" data-testid="sch-add-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="full">{L("Full (100%)", "Boursier (100%)")}</SelectItem>
                <SelectItem value="half">{L("Half (50%)", "Demi-bourse (50%)")}</SelectItem>
                <SelectItem value="special">{L("Special (fixed $)", "Spécial ($ fixe)")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {addType === "special" && (
            <div>
              <p className="text-xs text-muted-foreground mb-1">{L("Discount ($)", "Remise ($)")}</p>
              <Input type="number" value={addAmount} onChange={(e) => setAddAmount(e.target.value)} className="w-28" placeholder="$" data-testid="sch-add-amount" />
            </div>
          )}
          <Button onClick={addScholarship} data-testid="sch-add-apply">{L("Apply", "Appliquer")}</Button>
        </div>
      </div>

      <div className="flex flex-wrap gap-3 mb-6">
        <Select value={type} onValueChange={setType}>
          <SelectTrigger className="w-48" data-testid="sch-type-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{L("All types", "Tous les types")}</SelectItem>
            <SelectItem value="full">{L("Full", "Boursier")}</SelectItem>
            <SelectItem value="half">{L("Half", "Demi-bourse")}</SelectItem>
          </SelectContent>
        </Select>
        <Select value={level} onValueChange={setLevel}>
          <SelectTrigger className="w-64" data-testid="sch-level-filter"><SelectValue placeholder={L("Level", "Niveau")} /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{L("All levels", "Tous les niveaux")}</SelectItem>
            {GRADES.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <div className="bg-card border border-border rounded-lg p-4"><p className="text-xs text-muted-foreground">{L("Beneficiaries", "Bénéficiaires")}</p><p className="font-heading font-extrabold text-2xl" data-testid="sch-count">{data?.count ?? 0}</p></div>
        <div className="bg-card border border-border rounded-lg p-4"><p className="text-xs text-muted-foreground">{L("Full", "Boursier")}</p><p className="font-heading font-extrabold text-2xl text-emerald-600">{data?.full ?? 0}</p></div>
        <div className="bg-card border border-border rounded-lg p-4"><p className="text-xs text-muted-foreground">{L("Half", "Demi-bourse")}</p><p className="font-heading font-extrabold text-2xl text-emerald-500">{data?.half ?? 0}</p></div>
        <div className="bg-card border border-border rounded-lg p-4"><p className="text-xs text-muted-foreground">{L("Total discount", "Remise totale")}</p><p className="font-heading font-extrabold text-2xl text-primary" data-testid="sch-total">${(data?.total_discount ?? 0).toFixed(2)}</p></div>
      </div>

      {loading ? <Empty title="…" /> : rows.length === 0 ? <Empty title={L("No scholarship students", "Aucun élève boursier")} hint={L("Use « Grant a scholarship » above to add one.", "Utilisez « Accorder une bourse » ci-dessus pour en ajouter.")} /> : (
        <div className="border border-border rounded-lg overflow-x-auto">
          <Table>
            <TableHeader><TableRow>
              <TableHead>{L("Student", "Élève")}</TableHead>
              <TableHead>{L("Level", "Niveau")}</TableHead>
              <TableHead>{L("Type", "Type")}</TableHead>
              <TableHead className="text-right">{L("Total discount", "Remise totale")}</TableHead>
              <TableHead className="text-right">{L("Manage", "Gérer")}</TableHead>
            </TableRow></TableHeader>
            <TableBody>{rows.map((r) => (
              <TableRow key={r.student_id} data-testid={`sch-row-${r.student_id}`}>
                <TableCell className="font-medium">{r.name}</TableCell>
                <TableCell>{r.grade_level}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Select value={r.scholarship || "none"} onValueChange={(v) => setScholarship(r.student_id, v, amountOf(r.student_id))}>
                      <SelectTrigger className="w-32 h-8" data-testid={`sch-type-${r.student_id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">{L("None", "Aucune")}</SelectItem>
                        <SelectItem value="full">{L("Full", "Boursier")}</SelectItem>
                        <SelectItem value="half">{L("Half", "Demi-bourse")}</SelectItem>
                        <SelectItem value="special">{L("Special", "Spécial")}</SelectItem>
                      </SelectContent>
                    </Select>
                    {r.scholarship === "special" && <Input type="number" defaultValue={amountOf(r.student_id)} onBlur={(e) => setScholarship(r.student_id, "special", e.target.value)} className="w-20 h-8" placeholder="$" data-testid={`sch-amount-${r.student_id}`} />}
                  </div>
                </TableCell>
                <TableCell className="text-right font-semibold">${r.total_discount.toFixed(2)}</TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-2">
                    <Button size="sm" variant="outline" onClick={() => setPayStudent({ id: r.student_id, first_name: r.name.split(" ")[0], last_name: r.name.split(" ").slice(1).join(" ") })} data-testid={`sch-payments-${r.student_id}`}><Wallet className="h-3.5 w-3.5 mr-1" />{L("Waive payments", "Exonérer versements")}</Button>
                    <Button size="sm" variant="ghost" className="text-destructive hover:text-destructive" onClick={() => { if (window.confirm(L(`Remove ${r.name} from the scholarship list?`, `Retirer ${r.name} de la liste des bourses ?`))) setScholarship(r.student_id, "none"); }} data-testid={`sch-remove-${r.student_id}`}><Trash2 className="h-3.5 w-3.5 mr-1" />{L("Remove", "Retirer")}</Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}</TableBody>
          </Table>
        </div>
      )}
      {payStudent && <StudentInstallmentsModal student={payStudent} onClose={() => { setPayStudent(null); load(); }} />}
    </div>
  );
}
